import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { api, errMsg, fmtDate, label } from "../lib/api";
import { useAuth } from "../lib/context";
import { Card, Btn, PageHead, Loading, Empty } from "../components/Shell";
import { StatusBadge, EnvBadge } from "../components/StatusBadge";
import { CheckCircle2, Video, FileDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function Timeline({ events }) {
  return (
    <ol className="relative space-y-5 border-l-2 border-indigo-100 pl-6" data-testid="status-timeline">
      {events.map((e) => (
        <li key={e.id} className="relative">
          <span className="absolute -left-[33px] top-1 h-4 w-4 rounded-full border-[3px] border-white sx-grad shadow" />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-mono text-[11px] uppercase text-slate-500">{label(e.kind)}</span>
            {e.to && <StatusBadge status={e.to} />}
            <span className="text-xs text-slate-500">{fmtDate(e.at)} · {e.source} · {e.actor}</span>
          </div>
          {e.details && Object.values(e.details).some((v) => v != null) && <div className="mt-0.5 font-mono text-[11px] text-slate-500">{Object.entries(e.details).filter(([, v]) => v != null).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}</div>}
        </li>
      ))}
    </ol>
  );
}

export default function AppStatus() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { config } = useAuth();
  const [d, setD] = useState(null);
  const [slot, setSlot] = useState("");
  const load = useCallback(() => api.get(`/applications/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loading />;
  const a = d.application;
  const vkycAllowed = a.category === "bank_account" && d.provider?.supports_vkyc && ["submitted", "kyc_pending", "bank_review"].includes(a.external_status);
  const startProduct = async (kind) => {
    try {
      const r = await api.post("/applications", { kind, provider: "sandbox_pg", business_id: a.business_id });
      nav(`/applications/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      {d.receipt && (
        <div className={`ticket grid overflow-hidden rounded-3xl bg-white shadow-xl shadow-violet-900/10 md:grid-cols-[auto_1fr] ${sp.get("ack") ? "fade-up" : ""}`} data-testid="submission-ack">
          <div className="flex flex-col items-center justify-center gap-2 bg-[#C6F432] px-10 py-6 text-[#0E0B1F]">
            <CheckCircle2 className="h-12 w-12" />
            <div className="font-display text-lg font-extrabold">Received</div>
          </div>
          <div className="border-dashed border-slate-200 p-6 md:border-l-2 md:px-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-violet-600">Application receipt</div>
            <div className="font-display mt-1 text-2xl font-extrabold text-[#0E0B1F]">Submitted for review</div>
            <div className="mt-4 grid gap-4 text-sm sm:grid-cols-3">
              <div><div className="text-xs text-slate-500">Acknowledgement no.</div><div className="font-mono font-semibold" data-testid="receipt-ack-number">{d.receipt.acknowledgement}</div></div>
              <div><div className="text-xs text-slate-500">Submitted on</div><div className="font-semibold">{d.receipt.submitted_at ? fmtDate(d.receipt.submitted_at) : "-"}</div></div>
              <div><div className="text-xs text-slate-500">{label(a.kind)}</div><div className="font-semibold">{d.provider?.name}</div></div>
            </div>
            <p className="mt-4 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900">This is <b>not</b> a bank or provider approval. We will update each status below only when evidence is received.</p>
          </div>
        </div>
      )}
      {d.failures?.length > 0 && (
        <Card className="border-red-300 bg-red-50" data-testid="admin-failure-panel">
          <div className="flex items-center gap-2 font-semibold text-red-900"><AlertTriangle className="h-5 w-5" /> Provider response (admin only)</div>
          {d.failures.map((f) => (
            <div key={f.id} className="mt-2 rounded-lg border border-red-200 bg-white p-3 text-sm" data-testid={`admin-failure-${f.id}`}>
              <div className="flex flex-wrap items-center gap-2"><StatusBadge status={f.status} /><span className="font-mono text-[11px] text-slate-500">{f.op} · {f.provider} · attempts {f.attempts} · corr {f.correlation_id}</span></div>
              {f.last_error && <div className="mt-1 font-mono text-xs text-red-800">{f.last_error}</div>}
              {f.missing_dependencies && <div className="mt-1 text-xs text-purple-800">Missing: {JSON.stringify(f.missing_dependencies)}</div>}
            </div>
          ))}
        </Card>
      )}
      <PageHead testid="status-title" title={a.app_number} sub={`${label(a.kind)} · ${d.provider?.name}`}>
        {["draft", "correction_required"].includes(a.internal_status) && d.viewer_is_owner && <Btn data-testid="continue-editing-btn" onClick={() => nav(`/applications/${id}`)}>{a.internal_status === "draft" ? "Continue application" : "Make corrections"}</Btn>}
      </PageHead>
      <div className="grid gap-4 md:grid-cols-3">
        <Card><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Internal review</div><div className="mt-2"><StatusBadge status={a.internal_status} testid="status-internal" /></div></Card>
        <Card><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{a.category === "bank_account" ? "Bank status" : "Provider status"}</div><div className="mt-2 flex flex-wrap gap-2"><StatusBadge status={a.external_status} testid="status-external" /><EnvBadge env={a.external_env} testid="status-env" /></div>
          {a.provider_ref && <div className="mt-2 font-mono text-xs text-slate-500" data-testid="provider-ref">Provider ref: {a.provider_ref}</div>}</Card>
        <Card><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Submitted versions</div><div className="mt-2 text-sm" data-testid="snapshot-count">{d.snapshots.length} immutable snapshot(s)</div>
          {d.snapshots.map((s) => <div key={s.id} className="font-mono text-[11px] text-slate-500">v{s.version} · {fmtDate(s.created_at)} · rules {s.ruleset_id} v{s.ruleset_version}</div>)}</Card>
      </div>
      {a.external_status === "assisted_pending" && <Card className="border-purple-300 bg-purple-50 text-sm text-purple-900" data-testid="assisted-note">This bank/provider is not connected by API. Our operations team will submit your application through the assisted route; the status will change only when the bank/provider confirms.</Card>}
      {d.deficiencies.length > 0 && (
        <Card>
          <h2 className="mb-2 font-semibold">Correction requests</h2>
          {d.deficiencies.map((x) => <div key={x.id} className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 text-sm" data-testid={`deficiency-${x.id}`}><StatusBadge status={x.status === "open" ? "correction_required" : "submitted"} prefix={x.status} /><b>{x.target}</b> {x.message} <span className="text-xs text-slate-500">({x.source})</span></div>)}
        </Card>
      )}
      {a.category === "bank_account" && (
        <Card data-testid="vkyc-card">
          <h2 className="flex items-center gap-2 font-semibold"><Video className="h-5 w-5" /> Video KYC</h2>
          {!d.provider?.supports_vkyc ? <p className="mt-1 text-sm text-slate-500">Not offered by this bank through our platform.</p>
            : a.vkyc ? <div className="mt-2 text-sm"><StatusBadge status={a.vkyc.status === "completed" ? "approved" : "pending"} prefix={a.vkyc.status} /> {a.vkyc.slot && <span className="ml-2">Slot: {a.vkyc.slot}</span>}{a.vkyc.note && <p className="mt-1 text-xs text-amber-700">{a.vkyc.note}</p>}</div>
            : <p className="mt-1 text-sm text-slate-500">{vkycAllowed ? "Schedule or launch your bank-hosted video KYC." : ["approved", "account_created", "active"].includes(a.external_status) ? "No video KYC step is pending." : "Available after the bank accepts your submission."}</p>}
          {vkycAllowed && (
            <div className="mt-3 flex flex-wrap gap-2">
              <input type="datetime-local" data-testid="vkyc-slot-input" className="min-h-[44px] rounded-lg border border-slate-300 px-3 text-sm" value={slot} onChange={(e) => setSlot(e.target.value)} />
              <Btn variant="outline" data-testid="vkyc-schedule-btn" onClick={async () => { try { await api.post(`/applications/${id}/vkyc`, { slot }); load(); } catch (e) { toast.error(errMsg(e)); } }}>{slot ? "Schedule" : "Launch now"}</Btn>
            </div>
          )}
        </Card>
      )}
      {a.kind === "existing_account" && a.internal_status === "internal_approved" && (
        <Card data-testid="product-selection">
          <h2 className="font-semibold">Apply for merchant products</h2>
          <p className="mb-3 text-sm text-slate-500">Your verified account can be used for settlement. Each product has its own application, consent and status.</p>
          <div className="flex flex-wrap gap-2">{["pg", "qr", "pos"].map((k) => <Btn key={k} variant="outline" data-testid={`apply-${k}-btn`} onClick={() => startProduct(k)}>Apply for {k.toUpperCase()}</Btn>)}</div>
        </Card>
      )}
      {d.forms.length > 0 && (
        <Card>
          <h2 className="mb-2 font-semibold">Generated forms (immutable copies)</h2>
          {d.forms.map((f) => <div key={f.id} className="flex items-center gap-2 py-1 text-sm"><span>{f.template_code} v{f.template_version}</span><span className="font-mono text-[11px] text-slate-500">sha256 {f.sha256.slice(0, 12)}…</span>
            <Btn variant="ghost" data-testid={`download-form-${f.id}`} onClick={async () => { const r = await api.get(`/generated-forms/${f.id}/download`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data)); }}><FileDown className="h-4 w-4" /></Btn></div>)}
        </Card>
      )}
      <Card><h2 className="mb-4 font-semibold">Status timeline</h2><Timeline events={d.events} /></Card>
    </div>
  );
}

export function ResumeTrack() {
  const [apps, setApps] = useState(null);
  const [num, setNum] = useState("");
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => { api.get("/applications").then((r) => setApps(r.data)); }, []);
  const track = async (e) => {
    e.preventDefault();
    try { const r = await api.get(`/track/${encodeURIComponent(num)}`); nav(`/applications/${r.data.id}/status`); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div className="space-y-6">
      <PageHead testid="resume-title" title="My applications" sub="Resume a draft or track status." />
      <Card id="track" className={loc.hash === "#track" ? "ring-2 ring-amber-500" : ""}>
        <form onSubmit={track} className="flex flex-wrap gap-2">
          <label htmlFor="trk" className="sr-only">Application number</label>
          <input id="trk" data-testid="track-number-input" placeholder="Application number e.g. DEMO-CA-20261017-00001" className="min-h-[44px] flex-1 rounded-lg border border-slate-300 px-3 font-mono text-sm" value={num} onChange={(e) => setNum(e.target.value)} />
          <Btn data-testid="track-submit-btn">Track</Btn>
        </form>
      </Card>
      {!apps ? <Loading /> : apps.length === 0 ? <Empty title="No applications yet" sub="Start a journey from the home page."><Link to="/"><Btn>Start</Btn></Link></Empty> : (
        <div className="space-y-2">
          {apps.map((a) => (
            <Card key={a.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`app-row-${a.id}`}>
              <span className="font-mono text-sm font-semibold">{a.app_number}</span>
              <span className="text-sm text-slate-600">{label(a.kind)} · {a.search_name || "Untitled"}</span>
              <StatusBadge status={a.internal_status} /> <StatusBadge status={a.external_status} /> <EnvBadge env={a.external_env} />
              <div className="ml-auto flex gap-2">
                {["draft", "correction_required"].includes(a.internal_status) && <Btn data-testid={`resume-${a.id}`} onClick={() => nav(`/applications/${a.id}`)}>Resume</Btn>}
                <Btn variant="outline" data-testid={`status-${a.id}`} onClick={() => nav(`/applications/${a.id}/status`)}>Status</Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
